import { useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { NetworkIcon } from "lucide-react";
import { type ChallengeIdeaFiltersState } from "stores/challengeIdeaFilters";

import { parseDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { DateTimeInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { type InputOnChangeData } from "simplydo/react/ui/input";
import { Input } from "simplydo/react/ui/input";
import { List, ListItem } from "simplydo/react/ui/list";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import util from "utils/utils";

import { PaginationWithLimiter, Sticky } from "components/lib/UI";

import type {
  ChallengeDetail,
  ChallengeIdeaListItem,
  ChallengeProjectBoardDefaultLane,
  ChallengeProjectLane,
} from "../../types";
import { DeleteNameContainer, DeleteNameItem } from "./Styles";

type PaginationProps = {
  limit: number;
  page: number;
  total: number;
  updateFilters: (filters: Partial<ChallengeIdeaFiltersState>) => void;
  limitValues: number[];
};

const Pagination = ({
  limit,
  page,
  total,
  updateFilters,
  limitValues,
}: PaginationProps) => (
  <Sticky
    bottom={15}
    style={{
      textAlign: "center",
      zIndex: 10,
      display: "flex",
      justifyContent: "center",
      marginTop: 10,
    }}
  >
    <PaginationWithLimiter
      limit={limit}
      page={page}
      total={total}
      limitValues={limitValues}
      updateLimit={(value) => updateFilters({ limit: value })}
      updatePage={(value) => updateFilters({ page: value })}
    />
  </Sticky>
);

type IdeaDeletionModalProps = {
  challengeId: string;
  ideasToDelete?: ChallengeIdeaListItem[];
  setIdeasToDelete: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
  deselectIdeas: (ideaIds: string[]) => void;
};

const IdeaDeletionModal = ({
  challengeId,
  ideasToDelete = [],
  setIdeasToDelete,
  deselectIdeas,
}: IdeaDeletionModalProps) => {
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const { t } = useTranslation();

  // Get all child ideas from all ideas to delete by flattening
  const allChildIdeas = useMemo(() => {
    const allChildren: ChallengeIdeaListItem[] = [];
    ideasToDelete.forEach((idea) => {
      if (idea.ownerChildren) {
        allChildren.push(...idea.ownerChildren);
      }
    });
    return allChildren;
  }, [ideasToDelete]);

  return (
    <Modal open={!!ideasToDelete.length} onClose={() => setIdeasToDelete([])}>
      <ModalHeader>Delete {t("generic.ideas")}</ModalHeader>
      <ModalContent scrolling>
        <p>You are choosing to delete the following {t("generic.ideas")}:</p>
        <List variant="bulleted" compact>
          {ideasToDelete.map((idea) => (
            <ListItem key={idea._id}>
              {idea.children ? <Icon icon={NetworkIcon} /> : null}
              <b>
                {idea.name}
                {idea.owner ? ` - ${idea.owner.profile.fullName}` : ""}
              </b>
            </ListItem>
          ))}
        </List>
        {allChildIdeas.length ? (
          <>
            <p>
              The following child {t("generic.ideas")} will be removed from the
              deleted merged {t("generic.ideas")}, and returned to the{" "}
              {t("generic.challenge")} as individual {t("generic.ideas")}.
            </p>
            <List variant="bulleted" compact>
              {allChildIdeas.map((idea) => (
                <ListItem key={idea._id}>
                  <b>{idea.name}</b>
                </ListItem>
              ))}
            </List>
            <p>
              Any comments left on the merged {t("generic.ideas")} will be
              deleted, but any comments left on the original{" "}
              {t("generic.ideas")} will be returned to them.
            </p>
          </>
        ) : null}
        <Message color="red">
          This action is irreversible. These {t("generic.ideas")} will be
          permanently deleted, and cannot be recovered. Please make sure this
          action is correct, and double check the {t("generic.ideas")} you have
          chosen.
        </Message>
        <DeleteNameContainer>
          <DeleteNameItem>
            <p>
              Type the word &apos;DELETE&apos; below to confirm you understand
              this.
            </p>
            <Input
              placeholder="DELETE"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
            />
          </DeleteNameItem>
        </DeleteNameContainer>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => setIdeasToDelete([])}>Cancel</Button>
        <Button
          disabled={deleteConfirm !== "DELETE"}
          color="red"
          onClick={() => {
            util
              .confirm(
                `Delete ${t("generic.ideas")}`,
                `Are you absolutely sure you want to permanently delete these ${t("generic.ideas")}?`,
              )
              .then(() => {
                api.ideas.bulkDelete(
                  ideasToDelete.map((i) => i._id),
                  () => {
                    api.setQueryData(
                      api.challenges.getIdeas,
                      [challengeId],
                      (prev) => ({
                        ...prev,
                        ideas: prev.ideas.filter(
                          (i) =>
                            ideasToDelete.map((i) => i._id).indexOf(i._id) ===
                            -1,
                        ),
                      }),
                    );
                    // Invalidate challenge query to update idea count
                    api.invalidateQueries(api.challenges.get, [challengeId]);
                    api.invalidateQueries(api.challenges.getIdeas, [
                      challengeId,
                    ]);
                    deselectIdeas(ideasToDelete.map((idea) => idea._id));
                    setIdeasToDelete([]);
                    toast.success(
                      `${t("common:capitalise", {
                        key: "generic.ideas",
                      })} deleted`,
                    );
                  },
                  (err) => toast.error(err.message),
                );
                setDeleteConfirm("");
              })
              .catch(() => {
                setDeleteConfirm("");
              });
          }}
        >
          Delete
        </Button>
      </ModalActions>
    </Modal>
  );
};

type IdeaManagementDeadlineModalProps = {
  managementDeadlineIdeas?: ChallengeIdeaListItem[];
  setManagementDeadlineIdeas: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
  updateIdeaProjectDeadlineMulti: (deadline: Date) => void;
};

const IdeaManagementDeadlineModal = ({
  managementDeadlineIdeas = [],
  setManagementDeadlineIdeas,
  updateIdeaProjectDeadlineMulti,
}: IdeaManagementDeadlineModalProps) => {
  const { t } = useTranslation();
  const now = new Date();
  const defaultDeadline = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const [managementDeadline, setManagementDeadline] = useState(defaultDeadline);
  return (
    <Modal
      open={!!managementDeadlineIdeas.length}
      onClose={() => setManagementDeadlineIdeas([])}
    >
      <ModalHeader>Set {t("generic.idea")} management deadline</ModalHeader>
      <ModalContent scrolling>
        {" "}
        <div style={{ marginBottom: "25px" }}>
          <p>
            You are setting a management deadline for the following{" "}
            {t("generic.ideas")}:
          </p>
          <List variant="bulleted" compact>
            {managementDeadlineIdeas.map((idea) => (
              <ListItem key={idea._id}>
                <b>{idea.name}</b>
                {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
              </ListItem>
            ))}
          </List>
        </div>
        <Divider />
        <div>
          <b>Deadline</b>
          <p>Set a project deadline for all selected {t("generic.ideas")}</p>
          <DateTimeInput
            value={managementDeadline}
            onChange={(
              _e: React.SyntheticEvent,
              { value }: InputOnChangeData,
            ) => {
              const next = parseDate(value, "dd/MM/yyyy HH:mm");
              if (next) setManagementDeadline(next);
            }}
            minDate={now}
          />
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => setManagementDeadlineIdeas([])}>Cancel</Button>
        <Button
          onClick={() => {
            updateIdeaProjectDeadlineMulti(managementDeadline);
            setManagementDeadlineIdeas([]);
          }}
          color="primary"
        >
          Set deadline
        </Button>
      </ModalActions>
    </Modal>
  );
};

type IdeaMoveToLaneModalProps = {
  moveLaneIdeas?: ChallengeIdeaListItem[];
  setMoveLaneIdeas: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
  allChallengeProjectLanes?: ChallengeProjectLane[];
  checkUpdateProjectLane: (
    ideaIds: string[],
    laneId: string,
    order?: number | null,
  ) => void;
  challenge: ChallengeDetail;
};

const IdeaMoveToLaneModal = ({
  moveLaneIdeas = [],
  setMoveLaneIdeas,
  allChallengeProjectLanes = [],
  checkUpdateProjectLane,
  challenge,
}: IdeaMoveToLaneModalProps) => {
  const { t } = useTranslation();
  const defaultLane = challenge.projectBoard?.defaultLane as
    | ChallengeProjectBoardDefaultLane
    | undefined;
  const [selectedLaneId, setSelectedLaneId] = useState(
    defaultLane?._id ?? "default",
  );

  return (
    <Modal open={!!moveLaneIdeas.length} onClose={() => setMoveLaneIdeas([])}>
      <ModalHeader>
        Move{" "}
        {util.pluralise(
          moveLaneIdeas.length,
          t("generic.idea"),
          t("generic.ideas"),
          false,
        )}{" "}
        to project lane
      </ModalHeader>

      <ModalContent scrolling>
        <p>
          You are moving the following{" "}
          {util.pluralise(
            moveLaneIdeas.length,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          )}{" "}
          to a new project lane:
        </p>
        <List variant="bulleted" compact>
          {moveLaneIdeas.map((idea) => (
            <ListItem key={idea._id}>
              <b>{idea.name}</b>
              {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
            </ListItem>
          ))}
        </List>

        <Divider />

        <b>Select lane</b>
        <p>
          Select the lane where these{" "}
          {util.pluralise(
            moveLaneIdeas.length,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          )}{" "}
          will be moved.
        </p>
        <Dropdown
          variant="outline"
          fluid
          value={selectedLaneId}
          options={[
            {
              key: "default",
              value: "default",
              text: defaultLane?.name ?? "Default",
            },
            ...allChallengeProjectLanes.map((lane) => ({
              key: lane._id,
              value: lane._id,
              text: lane.name,
            })),
          ]}
          onChange={(_e, { value }) => setSelectedLaneId(String(value))}
        />
      </ModalContent>

      <ModalActions>
        <Button onClick={() => setMoveLaneIdeas([])}>Cancel</Button>
        <Button
          onClick={() => {
            checkUpdateProjectLane(
              moveLaneIdeas.map((idea) => idea._id!),
              selectedLaneId,
            );
            setMoveLaneIdeas([]);
          }}
          color="primary"
        >
          Move ideas
        </Button>
      </ModalActions>
    </Modal>
  );
};

export {
  IdeaDeletionModal,
  IdeaManagementDeadlineModal,
  IdeaMoveToLaneModal,
  Pagination,
};
